'use client';

import React from 'react';
import { Toast, ToastTitle, ToastDescription, ToastClose, Toaster as ToasterViewport } from '@/components/ui/toast';
import { useToast, type ToastPosition } from '@/hooks/use-toast';

interface ToasterProps {
  position?: ToastPosition;
}

/**
 * Toaster Component
 *
 * Wrapper component that provides the toast provider and manages the toast queue.
 * This should be placed once at the root of your application (e.g., in the layout).
 *
 * Usage:
 * 1. Place this component in your root layout:
 *    ```tsx
 *    // app/layout.tsx
 *    export default function Layout({ children }) {
 *      return (
 *        <html>
 *          <body>
 *            {children}
 *            <Toaster position="bottom-right" />
 *          </body>
 *        </html>
 *      );
 *    }
 *    ```
 *
 * 2. Use the hook in your components:
 *    ```tsx
 *    'use client';
 *    import { useToast } from '@/hooks/use-toast';
 *
 *    export function MyComponent() {
 *      const { toast } = useToast();
 *
 *      return (
 *        <button
 *          onClick={() => {
 *            toast({
 *              title: 'Success!',
 *              description: 'Your action completed successfully.',
 *              variant: 'success',
 *            });
 *          }}
 *        >
 *          Show Toast
 *        </button>
 *      );
 *    }
 *    ```
 */
export function Toaster({ position = 'bottom-right' }: ToasterProps) {
  const { toasts, dismiss } = useToast();

  return (
    <ToasterViewport position={position}>
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          variant={toast.variant}
          open={toast.open}
          onOpenChange={(open) => {
            if (!open) {
              dismiss(toast.id);
            }
          }}
        >
          {toast.title && <ToastTitle>{toast.title}</ToastTitle>}
          {toast.description && <ToastDescription>{toast.description}</ToastDescription>}
          {toast.action && (
            <button
              onClick={toast.action.onClick}
              className="inline-flex h-8 items-center justify-center rounded-md px-3 text-xs font-medium ring-offset-background-primary transition-colors hover:opacity-75 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
            >
              {toast.action.label}
            </button>
          )}
          <ToastClose />
        </Toast>
      ))}
    </ToasterViewport>
  );
}
